import { ErrorModal, type MenuOptionType } from "@dexteel/mesf-core";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import LaunchIcon from "@mui/icons-material/Launch";
import ReplayIcon from "@mui/icons-material/Replay";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import { ConfirmationDialogRaw } from "@/controls/modals/ConfirmationDialogRaw";
import { updateWorkOrderTiming } from "@/shared/repositories/WorkOrderRepository";
import { useDelayAssetsGrouped } from "@/shared/selectors/AssetButtonGroup";
import { toSlug } from "@/shared/utils/assets";
import { WorkOrderTimingModal } from "../components/WorkOrderTimingModal";
import { invalidateWorkOrderQueries } from "./invalidateWorkOrderQueries";

export const GANTT_WO_MENU_ID = "ganttWorkOrdersMenu";

type GanttWO = {
	WorkOrderId: number;
	AssetId: number;
	ShiftId: number;
	Status: string;
};

// WO band context menu; register with registerConfig({ id: GANTT_WO_MENU_ID, getOptions })
export const useWorkOrderGanttMenu = () => {
	const queryClient = useQueryClient();
	const [timingTarget, setTimingTarget] = useState<{
		workOrderId: number;
		assetId: number;
	} | null>(null);
	const [quick, setQuick] = useState<{
		type: "complete" | "reopen";
		wo: GanttWO;
	} | null>(null);
	const [error, setError] = useState("");
	const navigate = useNavigate();

	// getOptions is registered once, so the asset list is read through a ref at click time
	const { data: delayAssets } = useDelayAssetsGrouped();
	const assetsRef = useRef(delayAssets);
	useEffect(() => {
		assetsRef.current = delayAssets;
	}, [delayAssets]);

	const invalidateAll = () => invalidateWorkOrderQueries(queryClient);

	const runQuick = async (action: {
		type: "complete" | "reopen";
		wo: GanttWO;
	}) => {
		try {
			const resp = await updateWorkOrderTiming(
				action.wo.WorkOrderId,
				null,
				action.type === "complete" ? new Date() : null,
				action.type === "complete" ? "Completed" : "Running",
			);
			if (!resp.ok) throw new Error(resp.message);
			await invalidateAll();
		} catch (e) {
			setError((e as Error).message);
		}
	};

	const getOptions = useCallback((data: GanttWO): MenuOptionType[] => {
		if (!data || data.WorkOrderId == null) return [];
		const options: MenuOptionType[] = [
			{
				name: "Edit Times",
				key: "edit_times",
				onClick: () =>
					setTimingTarget({
						workOrderId: data.WorkOrderId,
						assetId: data.AssetId,
					}),
				icon: <AccessTimeIcon />,
			},
		];
		if (data.Status === "Running") {
			options.push({
				name: "Complete Now",
				key: "complete_now",
				onClick: () => setQuick({ type: "complete", wo: data }),
				icon: <CheckCircleOutlineIcon />,
			});
		}
		if (data.Status === "Completed") {
			options.push({
				name: "Reopen (still running)",
				key: "reopen",
				onClick: () => setQuick({ type: "reopen", wo: data }),
				icon: <ReplayIcon />,
			});
		}
		options.push({
			name: "See in Manager",
			key: "see_in_manager",
			onClick: () => {
				const match = assetsRef.current?.find(
					(a) => a.AssetId === data.AssetId,
				);
				const base = match
					? `/work-orders/${toSlug(match.AssetName)}`
					: "/work-orders";
				navigate(
					`${base}?shiftId=${data.ShiftId}&workOrderId=${data.WorkOrderId}`,
				);
			},
			icon: <LaunchIcon />,
		});
		return options;
		// biome-ignore lint/correctness/useExhaustiveDependencies: navigate is stable
	}, []);

	const menu = (
		<>
			<WorkOrderTimingModal
				open={timingTarget !== null}
				workOrderId={timingTarget?.workOrderId ?? null}
				assetId={timingTarget?.assetId ?? null}
				onClose={() => setTimingTarget(null)}
			/>
			<ConfirmationDialogRaw
				open={quick != null}
				title={
					quick?.type === "complete"
						? "Complete this work order now?"
						: "Reopen this work order?"
				}
				message={
					quick?.type === "complete"
						? "The order is completed with the current time as its end. Use Edit Times afterwards if the real end was different. Continue?"
						: "Clearing the end marks the order as still running — its window stays open until it is completed again. Continue?"
				}
				onClose={(value) => {
					const action = quick;
					setQuick(null);
					if (value === "OK" && action) void runQuick(action);
				}}
			/>
			<ErrorModal error={error} onHide={() => setError("")} />
		</>
	);

	return { getOptions, menu };
};
